// ReceivablesReports.tsx — wireframe S8 "Claims receivables ageing" plus
// the related reports its own callout names (TAT performance, query
// backlog, disallowances by reason code, settled vs billed, TDS
// reconciliation). Insurance Claims Phase 7. Every figure comes from
// /api/claims/reports/* (routes/claims-reports.js); nothing is recomputed
// here. Branch-scoped — the report covers the branch in the top bar.
import { useState } from 'react';
import { ApiError } from '@/lib/api/client';
import { Section } from '@/components/ui/Section';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Badge } from '@/components/ui/Badge';
import { KpiTile } from '@/components/ui/KpiTile';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { LoadingState, ErrorState } from '@/components/ui/States';
import { Empty } from '@/components/ui/Section';
import {
  useAgeingReport, useTatReport, useQueryBacklog, useDisallowanceReport, useSettledVsBilled, useTdsReport, downloadReportCsv,
  type AgeingParams, type AgeingBucket, type RangeParams,
} from './reportsApi';
import { headLabel, SHEET_TABLES } from './headLabels';

const inr = (v: number | string | null | undefined) => {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return n == null || Number.isNaN(n) ? '—' : n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
};
const lakh = (v: number) => (v >= 100000 ? `₹${(v / 100000).toFixed(1)} L` : `₹${inr(v)}`);
const todayIso = () => new Date().toISOString().slice(0, 10);
const daysAgoIso = (n: number) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
const BUCKETS: Array<[AgeingBucket, string]> = [['0_30', '0–30 d'], ['31_60', '31–60 d'], ['61_90', '61–90 d'], ['90_plus', '90+ d']];

function QueryError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return <ErrorState message={error instanceof ApiError ? error.message : 'Could not load this report'} onRetry={onRetry} />;
}

function AgeingTab() {
  const [params, setParams] = useState<AgeingParams>({ as_on: todayIso(), basis: 'discharge', group_by: 'payer', mode: '' });
  const [csvError, setCsvError] = useState('');
  const q = useAgeingReport(params);
  const set = (patch: Partial<AgeingParams>) => setParams((p) => ({ ...p, ...patch }));
  const r = q.data;
  return (
    <Section
      title="Claims receivables ageing"
      subtitle="Due = payer share − insurer payments (incl. TDS) on each invoice of open claims."
      action={
        <Button size="sm" variant="ghost" onClick={() => downloadReportCsv('ageing', params, `claims_ageing_${params.as_on}.csv`).catch((e: unknown) => setCsvError(e instanceof Error ? e.message : String(e)))}>
          ⬇ Export CSV
        </Button>
      }
    >
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <FormField label="As on"><Input type="date" value={params.as_on} max={todayIso()} onChange={(e) => set({ as_on: e.target.value })} className="w-40" /></FormField>
        <FormField label="Group by">
          <NativeSelect value={params.group_by} onChange={(e) => set({ group_by: e.target.value as 'payer' | 'mode' })} className="w-32">
            <option value="payer">Payer</option><option value="mode">Mode</option>
          </NativeSelect>
        </FormField>
        <FormField label="Mode">
          <NativeSelect value={params.mode} onChange={(e) => set({ mode: e.target.value })} className="w-36">
            <option value="">All</option><option value="cashless">Cashless</option><option value="scheme">Govt scheme</option><option value="corporate">Corporate</option>
          </NativeSelect>
        </FormField>
        <FormField label="Age counted from">
          <NativeSelect value={params.basis} onChange={(e) => set({ basis: e.target.value as 'discharge' | 'submission' })} className="w-40">
            <option value="discharge">Discharge date</option><option value="submission">Submission date</option>
          </NativeSelect>
        </FormField>
      </div>
      {csvError && <div className="mb-2 text-[12px] text-danger-ink">{csvError}</div>}
      {q.isLoading && <LoadingState label="Loading ageing…" />}
      {q.isError && <QueryError error={q.error} onRetry={() => q.refetch()} />}
      {r && (r.rows.length === 0 ? <Empty label="Nothing receivable" hint="No discharged claim has an amount still due from a payer as on this date." /> : (
        <>
          <div className="mb-3 grid grid-cols-2 gap-2.5 sm:grid-cols-4">
            <KpiTile size="sm" icon="💰" label="Total due" value={lakh(r.totals.total_due)} sub={`${r.totals.claims} claims`} />
            <KpiTile size="sm" icon="🟢" label="0–30 days" value={lakh(r.totals.buckets['0_30'])} />
            <KpiTile size="sm" icon="🟠" label="61–90 days" value={lakh(r.totals.buckets['61_90'])} />
            <KpiTile size="sm" icon="🔴" label="90+ days" value={lakh(r.totals.buckets['90_plus'])} />
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{r.group_by === 'mode' ? 'Mode' : 'Payer / TPA'}</TableHead>
                <TableHead className="text-right">Claims</TableHead>
                {BUCKETS.map(([, label]) => <TableHead key={label} className="text-right">{label}</TableHead>)}
                <TableHead className="text-right">Total due</TableHead>
                <TableHead className="text-right">Open queries</TableHead>
                <TableHead className="text-right">Avg settle days</TableHead>
                <TableHead className="text-right">Disallowance %</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {r.rows.map((row) => (
                <TableRow key={String(row.key)}>
                  <TableCell className="font-medium">{row.label}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.claims}</TableCell>
                  {BUCKETS.map(([b]) => (
                    <TableCell key={b} className={`text-right tabular-nums ${b === '90_plus' && row.buckets[b] > 0 ? 'text-danger-ink' : ''}`}>{inr(row.buckets[b])}</TableCell>
                  ))}
                  <TableCell className="text-right font-semibold tabular-nums">{inr(row.total_due)}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.open_queries}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.avg_settle_days ?? '—'}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.disallowance_pct != null ? `${row.disallowance_pct} %` : '—'}</TableCell>
                </TableRow>
              ))}
              <TableRow className="font-semibold">
                <TableCell>Total</TableCell>
                <TableCell className="text-right tabular-nums">{r.totals.claims}</TableCell>
                {BUCKETS.map(([b]) => <TableCell key={b} className="text-right tabular-nums">{inr(r.totals.buckets[b])}</TableCell>)}
                <TableCell className="text-right tabular-nums">{inr(r.totals.total_due)}</TableCell>
                <TableCell className="text-right tabular-nums">{r.totals.open_queries}</TableCell>
                <TableCell /><TableCell />
              </TableRow>
            </TableBody>
          </Table>
          <div className="mt-2 text-[11px] text-ink-muted">
            Avg settle days = discharge to first settlement received. Disallowance % = amount the payer deducted at settlement ÷ amount it
            approved, on settled claims. A past “as on” date counts payments up to that date, but uses today’s payer shares (a later
            recovery or write-off reduces the share in place).
          </div>
        </>
      ))}
    </Section>
  );
}

function RangeBar({ value, onChange }: { value: Required<RangeParams>; onChange: (v: Required<RangeParams>) => void }) {
  return (
    <div className="mb-3 flex flex-wrap items-end gap-3">
      <FormField label="From"><Input type="date" value={value.from} onChange={(e) => onChange({ ...value, from: e.target.value })} className="w-40" /></FormField>
      <FormField label="To"><Input type="date" value={value.to} onChange={(e) => onChange({ ...value, to: e.target.value })} className="w-40" /></FormField>
    </div>
  );
}

function TatTab() {
  const [range, setRange] = useState({ from: daysAgoIso(30), to: todayIso() });
  const q = useTatReport(range);
  return (
    <Section title="TAT performance" subtitle="Authorisations decided in the period. Targets: pre-auth 60 min, final discharge authorisation 180 min (design §1.5).">
      <RangeBar value={range} onChange={setRange} />
      {q.isLoading && <LoadingState label="Loading…" />}
      {q.isError && <QueryError error={q.error} onRetry={() => q.refetch()} />}
      {q.data && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Authorisation</TableHead><TableHead className="text-right">Target</TableHead><TableHead className="text-right">Decided</TableHead>
              <TableHead className="text-right">Median</TableHead><TableHead className="text-right">Within target</TableHead><TableHead className="text-right">Slowest</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {([['preauth', 'Pre-auth (initial + enhancement)'], ['final', 'Final authorisation']] as const).map(([k, label]) => {
              const g = q.data[k];
              return (
                <TableRow key={k}>
                  <TableCell className="font-medium">{label}</TableCell>
                  <TableCell className="text-right">{g.target_min} min</TableCell>
                  <TableCell className="text-right tabular-nums">{g.decided}</TableCell>
                  <TableCell className="text-right tabular-nums">{g.median_min != null ? `${g.median_min} min` : '—'}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {g.pct_within_target != null
                      ? <Badge variant={g.pct_within_target >= 90 ? 'success' : g.pct_within_target >= 70 ? 'warn' : 'danger'}>{g.pct_within_target} % ({g.within_target})</Badge>
                      : '—'}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{g.max_min != null ? `${g.max_min} min` : '—'}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Section>
  );
}

function QueriesTab() {
  const q = useQueryBacklog();
  return (
    <Section title="Query backlog" subtitle={q.data ? `${q.data.open} open · ${q.data.overdue} past their reply deadline` : undefined}>
      {q.isLoading && <LoadingState label="Loading…" />}
      {q.isError && <QueryError error={q.error} onRetry={() => q.refetch()} />}
      {q.data && (q.data.rows.length === 0 ? <Empty label="No open payer queries" /> : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Claim</TableHead><TableHead>Patient</TableHead><TableHead>Payer</TableHead><TableHead>Stage</TableHead>
              <TableHead>Received</TableHead><TableHead>Reply due</TableHead><TableHead>Query</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {q.data.rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-medium">{r.claim_ref}</TableCell>
                <TableCell>{r.patient_name ?? '—'}<div className="text-[10.5px] text-ink-muted">{r.uhid}</div></TableCell>
                <TableCell>{r.payer_name ?? '—'}</TableCell>
                <TableCell>{r.stage}</TableCell>
                <TableCell className="text-[12px]">{new Date(r.received_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</TableCell>
                <TableCell>
                  {r.due_at
                    ? <Badge variant={r.overdue ? 'danger' : 'warn'}>{new Date(r.due_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</Badge>
                    : '—'}
                </TableCell>
                <TableCell className="max-w-[320px] text-[12px]">{r.query_text}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ))}
    </Section>
  );
}

function DisallowancesTab() {
  const [range, setRange] = useState({ from: daysAgoIso(90), to: todayIso() });
  const q = useDisallowanceReport(range);
  return (
    <Section title="Disallowances by reason code" subtitle="Deductions from authorisation letters (decided in the period) and at settlement (received in the period).">
      <RangeBar value={range} onChange={setRange} />
      {q.isLoading && <LoadingState label="Loading…" />}
      {q.isError && <QueryError error={q.error} onRetry={() => q.refetch()} />}
      {q.data && (q.data.rows.length === 0 ? <Empty label="No disallowances in this period" /> : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Reason code</TableHead><TableHead className="text-right">Count</TableHead><TableHead className="text-right">Amount</TableHead>
              <TableHead className="text-right">At authorisation</TableHead><TableHead className="text-right">Recovered</TableHead>
              <TableHead className="text-right">Written off</TableHead><TableHead className="text-right">Under appeal</TableHead><TableHead>Heads</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {q.data.rows.map((r) => (
              <TableRow key={r.reason_code}>
                <TableCell className="font-medium">{r.reason_code}</TableCell>
                <TableCell className="text-right tabular-nums">{r.count}</TableCell>
                <TableCell className="text-right font-semibold tabular-nums">{inr(r.amount)}</TableCell>
                <TableCell className="text-right tabular-nums">{inr(r.at_authorization)}</TableCell>
                <TableCell className="text-right tabular-nums">{inr(r.recovered)}</TableCell>
                <TableCell className="text-right tabular-nums">{inr(r.written_off)}</TableCell>
                <TableCell className="text-right tabular-nums">{inr(r.appealed)}</TableCell>
                <TableCell className="text-[12px]">{r.heads.map(headLabel).join(', ') || '—'}</TableCell>
              </TableRow>
            ))}
            <TableRow className="font-semibold"><TableCell>Total</TableCell><TableCell /><TableCell className="text-right tabular-nums">{inr(q.data.total)}</TableCell><TableCell colSpan={5} /></TableRow>
          </TableBody>
        </Table>
      ))}
    </Section>
  );
}

function SettledVsBilledTab() {
  const [range, setRange] = useState({ from: daysAgoIso(90), to: todayIso() });
  const q = useSettledVsBilled(range);
  return (
    <Section title="Settled vs billed" subtitle="Claims with a settlement received in the period: amount claimed vs credited (net + TDS).">
      <RangeBar value={range} onChange={setRange} />
      {q.isLoading && <LoadingState label="Loading…" />}
      {q.isError && <QueryError error={q.error} onRetry={() => q.refetch()} />}
      {q.data && (q.data.rows.length === 0 ? <Empty label="No settlements in this period" /> : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Payer / TPA</TableHead><TableHead className="text-right">Claims</TableHead><TableHead className="text-right">Billed</TableHead>
              <TableHead className="text-right">Settled (net + TDS)</TableHead><TableHead className="text-right">TDS</TableHead><TableHead className="text-right">Net received</TableHead>
              <TableHead className="text-right">Short</TableHead><TableHead className="text-right">Settled %</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {q.data.rows.map((r) => (
              <TableRow key={String(r.key)}>
                <TableCell className="font-medium">{r.label}</TableCell>
                <TableCell className="text-right tabular-nums">{r.claims}</TableCell>
                <TableCell className="text-right tabular-nums">{inr(r.billed)}</TableCell>
                <TableCell className="text-right tabular-nums">{inr(r.gross_settled)}</TableCell>
                <TableCell className="text-right tabular-nums">{inr(r.tds)}</TableCell>
                <TableCell className="text-right tabular-nums">{inr(r.net_received)}</TableCell>
                <TableCell className={`text-right tabular-nums ${r.short > 0 ? 'text-warn-ink' : ''}`}>{inr(r.short)}</TableCell>
                <TableCell className="text-right tabular-nums">{r.settled_pct != null ? `${r.settled_pct} %` : '—'}</TableCell>
              </TableRow>
            ))}
            <TableRow className="font-semibold">
              <TableCell>Total</TableCell><TableCell className="text-right tabular-nums">{q.data.totals.claims}</TableCell>
              <TableCell className="text-right tabular-nums">{inr(q.data.totals.billed)}</TableCell><TableCell className="text-right tabular-nums">{inr(q.data.totals.gross_settled)}</TableCell>
              <TableCell className="text-right tabular-nums">{inr(q.data.totals.tds)}</TableCell><TableCell className="text-right tabular-nums">{inr(q.data.totals.net_received)}</TableCell>
              <TableCell className="text-right tabular-nums">{inr(q.data.totals.short)}</TableCell><TableCell />
            </TableRow>
          </TableBody>
        </Table>
      ))}
    </Section>
  );
}

function TdsTab() {
  const [range, setRange] = useState({ from: daysAgoIso(90), to: todayIso() });
  const [csvError, setCsvError] = useState('');
  const q = useTdsReport(range);
  return (
    <Section
      title="TDS reconciliation"
      subtitle="One row per settlement received — match against the payer's TDS certificate (Form 16A) and Form 26AS."
      action={
        <Button size="sm" variant="ghost" onClick={() => downloadReportCsv('tds', range, `claims_tds_${range.from}_${range.to}.csv`).catch((e: unknown) => setCsvError(e instanceof Error ? e.message : String(e)))}>
          ⬇ Export CSV
        </Button>
      }
    >
      <RangeBar value={range} onChange={setRange} />
      {csvError && <div className="mb-2 text-[12px] text-danger-ink">{csvError}</div>}
      {q.isLoading && <LoadingState label="Loading…" />}
      {q.isError && <QueryError error={q.error} onRetry={() => q.refetch()} />}
      {q.data && (q.data.rows.length === 0 ? <Empty label="No settlements in this period" /> : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Received</TableHead><TableHead>UTR</TableHead><TableHead>Payer</TableHead><TableHead>Claim</TableHead><TableHead>Patient</TableHead>
              <TableHead className="text-right">Gross approved</TableHead><TableHead className="text-right">TDS</TableHead><TableHead className="text-right">Net</TableHead><TableHead className="text-right">TDS %</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {q.data.rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{r.received_on}</TableCell><TableCell>{r.utr ?? '—'}</TableCell><TableCell>{r.payer_name ?? '—'}</TableCell>
                <TableCell>{r.claim_ref}{r.external_claim_no ? <div className="text-[10.5px] text-ink-muted">{r.external_claim_no}</div> : null}</TableCell>
                <TableCell>{r.patient_name ?? '—'}</TableCell>
                <TableCell className="text-right tabular-nums">{inr(r.gross_approved)}</TableCell>
                <TableCell className="text-right tabular-nums">{inr(r.tds)}</TableCell>
                <TableCell className="text-right tabular-nums">{inr(r.net_received)}</TableCell>
                <TableCell className="text-right tabular-nums">{r.tds_pct != null ? `${r.tds_pct} %` : '—'}</TableCell>
              </TableRow>
            ))}
            <TableRow className="font-semibold">
              <TableCell colSpan={5}>Total</TableCell>
              <TableCell className="text-right tabular-nums">{inr(q.data.totals.gross_approved)}</TableCell>
              <TableCell className="text-right tabular-nums">{inr(q.data.totals.tds)}</TableCell>
              <TableCell className="text-right tabular-nums">{inr(q.data.totals.net_received)}</TableCell><TableCell />
            </TableRow>
          </TableBody>
        </Table>
      ))}
    </Section>
  );
}

export function ReceivablesReports() {
  const [tab, setTab] = useState('ageing');
  return (
    <Tabs value={tab} onValueChange={setTab} className={`[&>[role=tabpanel]]:mt-0 ${SHEET_TABLES}`}>
      <TabsList variant="folder" className="mb-2.5">
        <TabsTrigger variant="folder" value="ageing">Ageing</TabsTrigger>
        <TabsTrigger variant="folder" value="tat">TAT performance</TabsTrigger>
        <TabsTrigger variant="folder" value="queries">Query backlog</TabsTrigger>
        <TabsTrigger variant="folder" value="disallowances">Disallowances</TabsTrigger>
        <TabsTrigger variant="folder" value="settled">Settled vs billed</TabsTrigger>
        <TabsTrigger variant="folder" value="tds">TDS reconciliation</TabsTrigger>
      </TabsList>
      <TabsContent value="ageing"><AgeingTab /></TabsContent>
      <TabsContent value="tat"><TatTab /></TabsContent>
      <TabsContent value="queries"><QueriesTab /></TabsContent>
      <TabsContent value="disallowances"><DisallowancesTab /></TabsContent>
      <TabsContent value="settled"><SettledVsBilledTab /></TabsContent>
      <TabsContent value="tds"><TdsTab /></TabsContent>
    </Tabs>
  );
}
